import React, { useState, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  Plus,
  Minus,
  Image,
  Upload,
  Save,
  ArrowLeft,
  ArrowRight,
  Package,
  Tag,
  DollarSign,
  Truck,
  Settings,
  Eye,
  HelpCircle,
  AlertCircle,
  Loader2,
  Trash2,
} from 'lucide-react'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { cn, formatCurrency } from '../../lib/utils'
import { productsApi, queryKeys } from '../../lib/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '../../components/ui/Button'
import { Input, Select, Textarea } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { useUIStore } from '../../stores/uiStore'
import toast from 'react-hot-toast'

// Validation schemas for each step
const step1Schema = z.object({
  name: z.string().min(2, 'نام کالا باید حداقل ۲ کاراکتر باشد').max(200),
  sku: z.string().optional().nullable(),
  barcode: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  category_id: z.preprocess(v => (!v ? null : v), z.string().optional().nullable()),
  brand_id: z.preprocess(v => (!v ? null : v), z.string().optional().nullable()),
  type: z.enum(['simple', 'variant', 'bundle', 'service', 'digital']),
  unit_id: z.preprocess(v => (!v ? null : v), z.string().optional().nullable()),
  is_active: z.boolean().default(true),
  is_sellable: z.boolean().default(true),
  is_purchasable: z.boolean().default(true),
  track_stock: z.boolean().default(true),
  weight: z.preprocess(v => (v === '' || v === null || v === undefined ? null : Number(v)), z.number().min(0).optional().nullable()),
  dimensions: z.object({
    length: z.number().min(0).optional(),
    width: z.number().min(0).optional(),
    height: z.number().min(0).optional(),
  }).optional(),
})

const step2Schema = z.object({
  variants: z.array(z.object({
    id: z.string().optional(),
    name: z.string().optional().nullable(),
    sku: z.string().optional().nullable(),
    barcode: z.string().optional(),
    attributes: z.record(z.string()).optional(),
    cost_price: z.preprocess(v => (v === '' || v === null || v === undefined ? null : Number(v)), z.number().min(0).optional().nullable()),
    sale_price: z.preprocess(v => (v === '' || v === null || v === undefined ? null : Number(v)), z.number().min(0).optional().nullable()),
    compare_at_price: z.preprocess(v => (v === '' || v === null || v === undefined ? null : Number(v)), z.number().min(0).optional().nullable()),
    current_stock: z.number().default(0),
    min_stock: z.number().default(0),
    max_stock: z.preprocess(v => (v === '' || v === null || v === undefined ? null : Number(v)), z.number().optional().nullable()),
    weight: z.preprocess(v => (v === '' || v === null || v === undefined ? null : Number(v)), z.number().min(0).optional().nullable()),
    images: z.array(z.string()).optional(),
    is_active: z.boolean().default(true),
  })).min(1, 'حداقل یک تنوع باید وجود داشته باشد'),
})

const step3Schema = z.object({
  cost_price: z.preprocess(v => (v === '' || v === null || v === undefined ? null : Number(v)), z.number().min(0, 'قیمت خرید باید مثبت باشد').optional().nullable()),
  sale_price: z.preprocess(v => (v === '' || v === null || v === undefined ? null : Number(v)), z.number().min(0, 'قیمت فروش باید مثبت باشد').optional().nullable()),
  compare_at_price: z.preprocess(v => (v === '' || v === null || v === undefined ? null : Number(v)), z.number().min(0).optional().nullable()),
  tax_rate: z.preprocess(v => (v === '' || v === null || v === undefined ? 0 : Number(v)), z.number().min(0).max(100).optional().nullable()),
  tax_included: z.boolean().default(false),
  currency: z.string().default('IRR'),
})

const step4Schema = z.object({
  track_stock: z.boolean().default(true),
  current_stock: z.preprocess(v => (v === '' || v === null || v === undefined ? 0 : Number(v)), z.number().min(0).default(0)),
  min_stock: z.preprocess(v => (v === '' || v === null || v === undefined ? 0 : Number(v)), z.number().min(0).default(0)),
  max_stock: z.preprocess(v => (v === '' || v === null || v === undefined ? null : Number(v)), z.number().min(0).optional().nullable()),
  reorder_point: z.preprocess(v => (v === '' || v === null || v === undefined ? null : Number(v)), z.number().min(0).optional().nullable()),
  reorder_qty: z.preprocess(v => (v === '' || v === null || v === undefined ? null : Number(v)), z.number().min(1).optional().nullable()),
  default_warehouse_id: z.preprocess(v => (!v ? null : v), z.string().optional().nullable()),
  allow_backorder: z.boolean().default(false),
  serial_tracked: z.boolean().default(false),
  batch_tracked: z.boolean().default(false),
  expiry_tracked: z.boolean().default(false),
})

const step5Schema = z.object({
  seo_title: z.string().max(60).optional(),
  seo_description: z.string().max(160).optional(),
  tags: z.array(z.string()).optional(),
  notes: z.string().optional(),
})

const stepSchemas = [step1Schema, step2Schema, step3Schema, step4Schema, step5Schema]

// Combined schema
const productWizardSchema = step1Schema.merge(step2Schema).merge(step3Schema).merge(step4Schema).merge(step5Schema)

type ProductWizardForm = z.infer<typeof productWizardSchema>

const steps = [
  { id: 'basic', title: 'اطلاعات پایه', icon: Package, description: 'نام، SKU، دسته‌بندی، نوع کالا' },
  { id: 'variants', title: 'تنوع‌ها', icon: Tag, description: 'تنوع‌های کالا، ویژگی‌ها، قیمت‌ها' },
  { id: 'pricing', title: 'قیمت‌گذاری', icon: DollarSign, description: 'قیمت خرید، فروش، مالیات' },
  { id: 'inventory', title: 'انبارداری', icon: Truck, description: 'موجودی، انبار، نقاط سفارش' },
  { id: 'advanced', title: 'پیشرفته', icon: Settings, description: 'SEO، برچسب‌ها، یادداشت‌ها' },
]

const variantAttributes = [
  { key: 'color', label: 'رنگ', type: 'color' },
  { key: 'size', label: 'سایز', type: 'text' },
  { key: 'material', label: 'ماده', type: 'text' },
  { key: 'capacity', label: 'ظرفیت', type: 'text' },
  { key: 'style', label: 'سبک', type: 'text' },
]

export default function ProductCreateWizard() {
  const navigate = useNavigate()
  const { closeModal } = useUIStore()
  const queryClient = useQueryClient()
  const [currentStep, setCurrentStep] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [images, setImages] = useState<string[]>([])
  const [variantImages, setVariantImages] = useState<Record<string, string[]>>({})

  const methods = useForm<ProductWizardForm>({
    defaultValues: {
      name: '',
      sku: '',
      barcode: '',
      description: '',
      category_id: null,
      brand_id: null,
      type: 'simple',
      unit_id: null,
      is_active: true,
      is_sellable: true,
      is_purchasable: true,
      track_stock: true,
      weight: null,
      dimensions: { length: 0, width: 0, height: 0 },
      variants: [
        {
          name: 'پیش‌فرض',
          sku: '',
          barcode: '',
          attributes: {},
          cost_price: null,
          sale_price: null,
          compare_at_price: null,
          current_stock: 0,
          min_stock: 0,
          max_stock: null,
          weight: null,
          images: [],
          is_active: true,
        }
      ],
      cost_price: null,
      sale_price: null,
      compare_at_price: null,
      tax_rate: 9,
      tax_included: false,
      currency: 'IRR',
      current_stock: 0,
      min_stock: 0,
      max_stock: null,
      reorder_point: null,
      reorder_qty: null,
      default_warehouse_id: null,
      allow_backorder: false,
      serial_tracked: false,
      batch_tracked: false,
      expiry_tracked: false,
      seo_title: '',
      seo_description: '',
      tags: [],
      notes: '',
    },
    mode: 'onChange',
  })

  const { watch, setValue, control, register, handleSubmit, formState: { errors } } = methods

  const productType = watch('type')
  const variants = watch('variants')
  const trackStock = watch('track_stock')

  // Simple product: sync pricing from step3 to variant
  const simpleVariant = variants[0]
  const simpleCostPrice = watch('cost_price')
  const simpleSalePrice = watch('sale_price')
  const simpleCompareAtPrice = watch('compare_at_price')

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: ProductWizardForm) => productsApi.create(data),
    onSuccess: (product) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.products() })
      toast.success('کالا با موفقیت ایجاد شد')
      navigate(`/inventory/products/${product.id}`)
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.detail || 'ایجاد کالا ناموفق بود')
    },
  })

  // Validate individual step
  const validateStep = (stepIndex: number): boolean => {
    console.log(`[ProductWizard] Attempting to validate Step ${stepIndex + 1}...`)

    // Step 1 check: if user provided a name but SKU is empty, auto-generate SKU
    if (stepIndex === 0) {
      const currentName = methods.getValues('name')
      let currentSku = methods.getValues('sku')
      if ((!currentSku || !currentSku.trim()) && currentName && currentName.trim().length >= 2) {
        const generatedSku = 'SKU-' + Math.floor(100000 + Math.random() * 900000)
        methods.setValue('sku', generatedSku)
        console.log('[ProductWizard] Auto-generated SKU:', generatedSku)
        toast.success(`کد SKU به طور خودکار تولید شد: ${generatedSku}`)
      }
    }

    // If product is simple, sync default variant SKU and Name from main product
    if (methods.getValues('type') === 'simple') {
      const mainSku = methods.getValues('sku') || 'SKU-DEFAULT'
      const mainName = methods.getValues('name') || 'محصول بدون نام'
      methods.setValue('variants.0.sku', mainSku)
      methods.setValue('variants.0.name', mainName)
    }

    methods.clearErrors()
    const currentValues = methods.getValues()
    const schema = stepSchemas[stepIndex]
    if (!schema) return true

    const result = schema.safeParse(currentValues)
    console.log(`[ProductWizard] Step ${stepIndex + 1} validation result:`, { success: result.success, values: currentValues })

    if (!result.success) {
      console.warn(`[ProductWizard] Validation failed on Step ${stepIndex + 1}:`, result.error.format())
      const errorMessages: string[] = []
      result.error.issues.forEach((issue) => {
        const fieldPath = issue.path.join('.') as any
        methods.setError(fieldPath, {
          type: 'manual',
          message: issue.message,
        })
        errorMessages.push(issue.message)
      })
      toast.error(`خطا در مرحله ${stepIndex + 1}: ${errorMessages.join(' | ')}`)
      return false
    }
    return true
  }

  // Navigation
  const nextStep = () => {
    console.log(`[ProductWizard] nextStep clicked on step ${currentStep + 1}`)
    const isValidStep = validateStep(currentStep)
    if (!isValidStep) {
      console.warn(`[ProductWizard] Cannot advance, step ${currentStep + 1} is invalid.`)
      return
    }

    // Sync simple product variant prices
    if (productType === 'simple' && currentStep === 2) {
      setValue('variants', variants.map((v) => ({
        ...v,
        cost_price: simpleCostPrice,
        sale_price: simpleSalePrice,
        compare_at_price: simpleCompareAtPrice,
      })))
    }

    if (currentStep < steps.length - 1) {
      console.log(`[ProductWizard] Moving to step ${currentStep + 2}`)
      setCurrentStep(prev => prev + 1)
    }
  }

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
    }
  }

  const goToStep = (index: number) => {
    if (index < currentStep) {
      setCurrentStep(index)
      return
    }
    for (let i = 0; i < index; i++) {
      if (!validateStep(i)) {
        setCurrentStep(i)
        return
      }
    }
    setCurrentStep(index)
  }

  // Variant management
  const { fields: variantFields, append: appendVariant, remove: removeVariant, move: moveVariant } = useFieldArray({
    control,
    name: 'variants',
  })

  const addVariant = () => {
    appendVariant({
      name: '',
      sku: '',
      barcode: '',
      attributes: {},
      cost_price: null,
      sale_price: null,
      compare_at_price: null,
      current_stock: 0,
      min_stock: 0,
      max_stock: null,
      weight: null,
      images: [],
      is_active: true,
    })
  }

  const deleteVariant = (index: number) => {
    if (variantFields.length <= 1) {
      toast.error('حداقل یک تنوع باید باقی بماند')
      return
    }
    removeVariant(index)
  }

  // Image upload
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>, target: 'main' | `variant-${number}`) => {
    const files = Array.from(e.target.files || [])
    const newImages: string[] = []

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`فایل ${file.name} باید کمتر از ۵ مگابایت باشد`)
        return
      }
      const reader = new FileReader()
      reader.onload = (event) => {
        newImages.push(event.target?.result as string)
        if (target === 'main') {
          setImages(prev => [...prev, event.target?.result as string])
        } else {
          const variantIndex = parseInt(target.split('-')[1])
          setVariantImages(prev => ({
            ...prev,
            [variantIndex]: [...(prev[variantIndex] || []), event.target?.result as string]
          }))
        }
      }
      reader.readAsDataURL(file)
    })
  }

  const removeImage = (index: number, target: 'main' | `variant-${number}`) => {
    if (target === 'main') {
      setImages(prev => prev.filter((_, i) => i !== index))
    } else {
      const variantIndex = parseInt(target.split('-')[1])
      setVariantImages(prev => ({
        ...prev,
        [variantIndex]: prev[variantIndex]?.filter((_, i) => i !== index) || []
      }))
    }
  }

  // Handle submit
  const onSubmit = async (data: ProductWizardForm) => {
    setIsSubmitting(true)
    try {
      // Prepare final data
      const submitData = {
        ...data,
        variants: data.type === 'simple' ? [{
          ...data.variants[0],
          cost_price: data.cost_price,
          sale_price: data.sale_price,
          compare_at_price: data.compare_at_price,
        }] : data.variants,
        images,
        // Remove wizard-only fields
        // type is kept for backend
      }
      createMutation.mutate(submitData)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="w-full min-h-screen bg-neutral-50 dark:bg-neutral-950 flex flex-col pb-24">
      {/* Progress Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 z-20 bg-white/95 dark:bg-neutral-900/95 backdrop-blur-md border-b border-neutral-200 dark:border-neutral-800 px-4 sm:px-6 py-3.5 shadow-sm"
      >
        {/* Step Indicator */}
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between overflow-x-auto gap-2 sm:gap-4 pb-1 scrollbar-none">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <button
                  onClick={() => index <= currentStep && goToStep(index)}
                  disabled={index > currentStep}
                  className={cn(
                    'flex items-center gap-2 transition-all duration-200 shrink-0 py-1 px-2 rounded-lg',
                    index === currentStep && 'bg-primary-50 dark:bg-primary-950/40 text-primary-700 dark:text-primary-300 font-semibold',
                    index < currentStep && 'text-primary-600 dark:text-primary-400 hover:bg-neutral-100 dark:hover:bg-neutral-800',
                    index > currentStep && 'text-neutral-400 dark:text-neutral-500 opacity-70 cursor-not-allowed'
                  )}
                  aria-current={index === currentStep ? 'step' : undefined}
                >
                  <div className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-200 shrink-0',
                    index < currentStep && 'bg-primary-600 text-white',
                    index === currentStep && 'bg-primary-600 text-white ring-2 ring-primary-300 dark:ring-primary-800',
                    index > currentStep && 'bg-neutral-200 dark:bg-neutral-800 text-neutral-500'
                  )}>
                    {index < currentStep ? <Check className="w-4 h-4" /> : index + 1}
                  </div>
                  <span className="text-xs sm:text-sm font-medium whitespace-nowrap">{step.title}</span>
                </button>
              </React.Fragment>
            ))}
          </div>

          {/* Progress Bar */}
          <div className="mt-2.5 h-1.5 bg-neutral-200 dark:bg-neutral-800 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
              transition={{ duration: 0.3 }}
              className="h-full bg-primary-600 rounded-full"
            />
          </div>

          {/* Step Description */}
          <p className="mt-2 text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 text-center">
            {steps[currentStep].description}
          </p>
        </div>
      </motion.div>

      {/* Form Content */}
      <form onSubmit={handleSubmit(onSubmit)} className="flex-1 px-4 sm:px-6 py-6" noValidate>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          className="max-w-4xl mx-auto w-full space-y-6"
        >
          <AnimatePresence mode="wait">
            {/* Step 1: Basic Info */}
            {currentStep === 0 && (
              <div className="space-y-6">
                <div className="card p-6">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
                    <Package className="w-5 h-5 text-primary-600" />
                    اطلاعات اصلی کالا
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Controller
                      name="name"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Input
                          label="نام کالا *"
                          placeholder="مثال: موبایل سامسونگ Galaxy A54"
                          error={errors.name?.message}
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="sku"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Input
                          label="SKU *"
                          placeholder="مثال: SAM-A54-BLK-128"
                          error={errors.sku?.message}
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="barcode"
                      control={control}
                      render={({ field }) => (
                        <Input
                          label="بارکد"
                          placeholder="بارکد انباره (اختیاری)"
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="type"
                      control={control}
                      render={({ field }) => (
                        <Select
                          label="نوع کالا *"
                          options={[
                            { value: 'simple', label: 'ساده (بدون تنوع)' },
                            { value: 'variant', label: 'متغییر (رنگ، سایز و... )' },
                            { value: 'bundle', label: 'بندل (ترکیب چند کالا)' },
                            { value: 'service', label: 'خدماتی' },
                            { value: 'digital', label: 'دیجیتال (دانلود/لایسنس)' },
                          ]}
                          placeholder="نوع کالا را انتخاب کنید"
                          error={errors.type?.message}
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="category_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          label="دسته‌بندی"
                          options={[
                            { value: '', label: 'انتخاب کنید' },
                            // Will be populated from API
                          ]}
                          placeholder="دسته‌بندی را انتخاب کنید"
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="brand_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          label="برند"
                          options={[
                            { value: '', label: 'انتخاب کنید' },
                          ]}
                          placeholder="برند را انتخاب کنید"
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="unit_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          label="واحد اندازه‌گیری"
                          options={[
                            { value: '', label: 'انتخاب کنید' },
                            { value: 'pcs', label: 'عدد' },
                            { value: 'kg', label: 'کیلوگرم' },
                            { value: 'm', label: 'متر' },
                            { value: 'box', label: 'جعبه' },
                            { value: 'pack', label: 'پک' },
                          ]}
                          placeholder="واحد را انتخاب کنید"
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="weight"
                      control={control}
                      render={({ field }) => (
                        <Input
                          label="وزن (گرم)"
                          type="number"
                          min={0}
                          step={0.01}
                          placeholder="اختیاری"
                          {...field}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Images Upload */}
                <div className="card p-6">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
                    <Image className="w-5 h-5 text-primary-600" />
                    تصاویر کالا
                  </h3>
                  <div className="border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-xl p-8">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handleImageUpload(e, 'main')}
                      className="hidden"
                      id="main-images"
                    />
                    <label htmlFor="main-images" className="cursor-pointer flex flex-col items-center gap-3">
                      <Upload className="w-10 h-10 text-neutral-400" />
                      <div className="text-center">
                        <p className="font-medium text-neutral-700 dark:text-neutral-300">
                          تصاویر را اینجا رها کنید یا کلیک کنید
                        </p>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">
                          حداکثر ۱۰ تصویر، هر کدام حداکثر ۵ مگابایت
                        </p>
                      </div>
                    </label>
                    {images.length > 0 && (
                      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {images.map((img, i) => (
                          <div key={i} className="relative aspect-square rounded-lg overflow-hidden">
                            <img src={img} alt={`Image ${i + 1}`} className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => removeImage(i, 'main')}
                              className="absolute top-1 left-1 w-6 h-6 rounded-full bg-danger-500/90 text-white flex items-center justify-center hover:bg-danger-600 transition-colors"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div className="card p-6">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">توضیحات کالا</h3>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <Textarea
                        label="توضیحات کامل"
                        placeholder="توضیحات کالا برای نمایش در فروشگاه و گزارش‌ها..."
                        rows={4}
                        {...field}
                      />
                    )}
                  />
                </div>

                {/* Status Toggles */}
                <div className="card p-6">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">تنظیمات وضعیت</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <label className="flex items-center gap-3 p-3 rounded-xl border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 cursor-pointer transition-colors">
                      <Controller
                        name="is_active"
                        control={control}
                        render={({ field }) => (
                          <input type="checkbox" className="w-5 h-5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500" {...field} />
                        )}
                      />
                      <span className="font-medium text-neutral-900 dark:text-white">فعال</span>
                    </label>
                    <label className="flex items-center gap-3 p-3 rounded-xl border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 cursor-pointer transition-colors">
                      <Controller
                        name="is_sellable"
                        control={control}
                        render={({ field }) => (
                          <input type="checkbox" className="w-5 h-5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500" {...field} />
                        )}
                      />
                      <span className="font-medium text-neutral-900 dark:text-white">قابل فروش</span>
                    </label>
                    <label className="flex items-center gap-3 p-3 rounded-xl border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 cursor-pointer transition-colors">
                      <Controller
                        name="is_purchasable"
                        control={control}
                        render={({ field }) => (
                          <input type="checkbox" className="w-5 h-5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500" {...field} />
                        )}
                      />
                      <span className="font-medium text-neutral-900 dark:text-white">قابل خرید</span>
                    </label>
                    <label className="flex items-center gap-3 p-3 rounded-xl border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 cursor-pointer transition-colors">
                      <Controller
                        name="track_stock"
                        control={control}
                        render={({ field }) => (
                          <input type="checkbox" className="w-5 h-5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500" {...field} />
                        )}
                      />
                      <span className="font-medium text-neutral-900 dark:text-white">رصد موجودی</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Variants */}
            {currentStep === 1 && (
              <div className="space-y-6">
                {productType === 'simple' ? (
                  <div className="card p-6 text-center">
                    <Package className="w-16 h-16 text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
                      کالاهای ساده تنوع ندارند
                    </h3>
                    <p className="text-neutral-500 dark:text-neutral-400 mb-4">
                      برای کالاهای ساده، اطلاعات قیمت و موجودی در مراحل بعدی پر می‌شود.
                      یک تنوع پیش‌فرض به‌صورت خودکار ایجاد می‌شود.
                    </p>
                    <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-xl p-4 text-right">
                      <p className="font-medium text-neutral-900 dark:text-white">تنوع پیش‌فرض</p>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        SKU: {watch('sku') || '—'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
                        <Tag className="w-5 h-5 text-primary-600" />
                        مدیریت تنوع‌ها
                      </h3>
                      {variantFields.length < 20 && (
                        <Button variant="outline" size="sm" type="button" onClick={addVariant}>
                          <Plus className="w-4 h-4" />
                          افزودن تنوع
                        </Button>
                      )}
                    </div>

                    {variantFields.map((variant, index) => (
                      <motion.div
                        key={variant.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="card p-5"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <span className="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-700 dark:text-primary-300 font-bold">
                              {index + 1}
                            </span>
                            <div>
                              <p className="font-medium text-neutral-900 dark:text-white">تنوع #{index + 1}</p>
                              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                                ویژگی‌ها و قیمت‌های این تنوع را مشخص کنید
                              </p>
                            </div>
                          </div>
                          {variantFields.length > 1 && (
                            <Button variant="ghost" size="xs" type="button" onClick={() => deleteVariant(index)} className="text-danger-600">
                              <Trash2 className="w-4 h-4" />
                              حذف
                            </Button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          <Controller
                            name={`variants.${index}.name`}
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                              <Input
                                label="نام تنوع *"
                                placeholder="مثال: مشکی / ۱۲۸ گیگابایت"
                                error={errors.variants?.[index]?.name?.message}
                                {...field}
                              />
                            )}
                          />
                          <Controller
                            name={`variants.${index}.sku`}
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                              <Input
                                label="SKU تنوع *"
                                placeholder="تولید خودکار از SKU اصلی"
                                error={errors.variants?.[index]?.sku?.message}
                                {...field}
                              />
                            )}
                          />
                          <Controller
                            name={`variants.${index}.barcode`}
                            control={control}
                            render={({ field }) => (
                              <Input
                                label="بارکد"
                                placeholder="اختیاری"
                                {...field}
                              />
                            )}
                          />
                          <Controller
                            name={`variants.${index}.is_active`}
                            control={control}
                            render={({ field }) => (
                              <label className="flex items-center gap-2 mt-6 cursor-pointer">
                                <input type="checkbox" className="w-5 h-5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500" {...field} />
                                <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">فعال</span>
                              </label>
                            )}
                          />
                        </div>

                        {/* Variant Attributes */}
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                            ویژگی‌های تنوع
                          </label>
                          <div className="flex flex-wrap gap-3">
                            {variantAttributes.map(attr => (
                              <Controller
                                key={attr.key}
                                name={`variants.${index}.attributes.${attr.key}`}
                                control={control}
                                render={({ field }) => (
                                  <div className="flex items-center gap-2 w-40">
                                    <span className="text-xs text-neutral-500 dark:text-neutral-400 w-20">{attr.label}</span>
                                    <Input
                                      placeholder={attr.label}
                                      className="text-xs"
                                      {...field}
                                    />
                                  </div>
                                )}
                              />
                            ))}
                          </div>
                        </div>

                        {/* Variant Prices */}
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                          <Controller
                            name={`variants.${index}.cost_price`}
                            control={control}
                            render={({ field }) => (
                              <Input
                                label="قیمت خرید (ریال)"
                                type="number"
                                min={0}
                                step={1000}
                                placeholder="۰"
                                {...field}
                              />
                            )}
                          />
                          <Controller
                            name={`variants.${index}.sale_price`}
                            control={control}
                            render={({ field }) => (
                              <Input
                                label="قیمت فروش (ریال)"
                                type="number"
                                min={0}
                                step={1000}
                                placeholder="۰"
                                {...field}
                              />
                            )}
                          />
                          <Controller
                            name={`variants.${index}.compare_at_price`}
                            control={control}
                            render={({ field }) => (
                              <Input
                                label="قیمت مقایسه (ریال)"
                                type="number"
                                min={0}
                                step={1000}
                                placeholder="اختیاری"
                                {...field}
                              />
                            )}
                          />
                        </div>

                        {/* Variant Stock */}
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                          <Controller
                            name={`variants.${index}.current_stock`}
                            control={control}
                            render={({ field }) => (
                              <Input
                                label="موجودی فعلی"
                                type="number"
                                min={0}
                                step={1}
                                {...field}
                              />
                            )}
                          />
                          <Controller
                            name={`variants.${index}.min_stock`}
                            control={control}
                            render={({ field }) => (
                              <Input
                                label="حداقل موجودی"
                                type="number"
                                min={0}
                                step={1}
                                {...field}
                              />
                            )}
                          />
                          <Controller
                            name={`variants.${index}.max_stock`}
                            control={control}
                            render={({ field }) => (
                              <Input
                                label="حداکثر موجودی"
                                type="number"
                                min={0}
                                step={1}
                                placeholder="اختیاری"
                                {...field}
                              />
                            )}
                          />
                        </div>

                        {/* Variant Images */}
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                            تصاویر این تنوع
                          </label>
                          <div className="border-2 border-dashed border-neutral-300 dark:border-neutral-700 rounded-xl p-4">
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={(e) => handleImageUpload(e, `variant-${index}`)}
                              className="hidden"
                              id={`variant-images-${index}`}
                            />
                            <label htmlFor={`variant-images-${index}`} className="cursor-pointer flex items-center justify-center gap-2 py-3 text-sm text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400">
                              <Upload className="w-4 h-4" />
                              افزودن تصویر برای این تنوع
                            </label>
                            {variantImages[index]?.length > 0 && (
                              <div className="mt-3 flex gap-2 overflow-x-auto">
                                {variantImages[index]!.map((img, i) => (
                                  <div key={i} className="relative w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                                    <img src={img} alt={`Variant ${index} Image ${i}`} className="w-full h-full object-cover" />
                                    <button
                                      type="button"
                                      onClick={() => removeImage(i, `variant-${index}`)}
                                      className="absolute top-1 left-1 w-5 h-5 rounded-full bg-danger-500/90 text-white flex items-center justify-center hover:bg-danger-600"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </>
                )}
              </div>
            )}

            {/* Step 3: Pricing */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="card p-6">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-primary-600" />
                    قیمت‌گذاری
                  </h3>

                  {productType === 'simple' ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Controller
                        name="cost_price"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="قیمت خرید (ریال) *"
                            type="number"
                            min={0}
                            step={1000}
                            placeholder="۰"
                            {...field}
                          />
                        )}
                      />
                      <Controller
                        name="sale_price"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="قیمت فروش (ریال) *"
                            type="number"
                            min={0}
                            step={1000}
                            placeholder="۰"
                            {...field}
                          />
                        )}
                      />
                      <Controller
                        name="compare_at_price"
                        control={control}
                        render={({ field }) => (
                          <Input
                            label="قیمت مقایسه / خط خورده (ریال)"
                            type="number"
                            min={0}
                            step={1000}
                            placeholder="اختیاری"
                            {...field}
                          />
                        )}
                      />
                    </div>
                  ) : (
                    <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-xl p-4 mb-4">
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        برای کالاهای متغییر، قیمت‌ها در مرحله «تنوع‌ها» برای هر تنوع به‌صورت جداگانه تعریف شده‌اند.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-neutral-200 dark:border-neutral-800">
                    <Controller
                      name="tax_rate"
                      control={control}
                      render={({ field }) => (
                        <Input
                          label="نرخ مالیات (%)"
                          type="number"
                          min={0}
                          max={100}
                          step={0.1}
                          placeholder="۹"
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="tax_included"
                      control={control}
                      render={({ field }) => (
                        <label className="flex items-center gap-2 mt-10 cursor-pointer">
                          <input type="checkbox" className="w-5 h-5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500" {...field} />
                          <span className="font-medium text-neutral-700 dark:text-neutral-300">قیمت‌ها شامل مالیات است</span>
                        </label>
                      )}
                    />
                    <Controller
                      name="currency"
                      control={control}
                      render={({ field }) => (
                        <Select
                          label="واحد پول"
                          options={[
                            { value: 'IRR', label: 'ریال ایران (IRR)' },
                            { value: 'USD', label: 'دلار آمریکا (USD)' },
                            { value: 'EUR', label: 'یورو (EUR)' },
                          ]}
                          {...field}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Price Preview */}
                <div className="card p-6 bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800">
                  <h4 className="font-medium text-primary-800 dark:text-primary-300 mb-3">پیش‌نمایش قیمت</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-primary-600 dark:text-primary-400">قیمت خرید</p>
                      <p className="font-bold text-neutral-900 dark:text-white">
                        {productType === 'simple'
                          ? formatCurrency(watch('cost_price') || 0)
                          : 'در تنوع‌ها تعریف شده'}
                      </p>
                    </div>
                    <div>
                      <p className="text-primary-600 dark:text-primary-400">قیمت فروش</p>
                      <p className="font-bold text-neutral-900 dark:text-white">
                        {productType === 'simple'
                          ? formatCurrency(watch('sale_price') || 0)
                          : 'در تنوع‌ها تعریف شده'}
                      </p>
                    </div>
                    <div>
                      <p className="text-primary-600 dark:text-primary-400">قیمت مقایسه</p>
                      <p className="font-bold text-neutral-900 dark:text-white">
                        {productType === 'simple'
                          ? (watch('compare_at_price') ? formatCurrency(watch('compare_at_price')!) : '—')
                          : 'در تنوع‌ها تعریف شده'}
                      </p>
                    </div>
                    <div>
                      <p className="text-primary-600 dark:text-primary-400">مالیات</p>
                      <p className="font-bold text-neutral-900 dark:text-white">{watch('tax_rate') || 9}%</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Inventory */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="card p-6">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
                    <Truck className="w-5 h-5 text-primary-600" />
                    تنظیمات انبارداری
                  </h3>

                  <Controller
                    name="track_stock"
                    control={control}
                    render={({ field }) => (
                      <label className="flex items-center gap-3 p-3 rounded-xl border border-neutral-200 dark:border-neutral-800 mb-4 cursor-pointer transition-colors">
                        <input type="checkbox" className="w-5 h-5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500" {...field} />
                        <span className="font-medium text-neutral-900 dark:text-white">رصد موجودی برای این کالا فعال باشد</span>
                      </label>
                    )}
                  />

                  {trackStock && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <Controller
                          name="current_stock"
                          control={control}
                          render={({ field }) => (
                            <Input
                              label="موجودی اولیه"
                              type="number"
                              min={0}
                              step={1}
                              {...field}
                            />
                          )}
                        />
                        <Controller
                          name="min_stock"
                          control={control}
                          render={({ field }) => (
                            <Input
                              label="حداقل موجودی (نقطه سفارش)"
                              type="number"
                              min={0}
                              step={1}
                              {...field}
                            />
                          )}
                        />
                        <Controller
                          name="max_stock"
                          control={control}
                          render={({ field }) => (
                            <Input
                              label="حداکثر موجودی"
                              type="number"
                              min={0}
                              step={1}
                              placeholder="اختیاری"
                              {...field}
                            />
                          )}
                        />
                        <Controller
                          name="reorder_point"
                          control={control}
                          render={({ field }) => (
                            <Input
                              label="نقطه سفارش مجدد"
                              type="number"
                              min={0}
                              step={1}
                              placeholder="اختیاری"
                              {...field}
                            />
                          )}
                        />
                        <Controller
                          name="reorder_qty"
                          control={control}
                          render={({ field }) => (
                            <Input
                              label="مقدار سفارش پیش‌فرض"
                              type="number"
                              min={1}
                              step={1}
                              placeholder="اختیاری"
                              {...field}
                            />
                          )}
                        />
                        <Controller
                          name="default_warehouse_id"
                          control={control}
                          render={({ field }) => (
                            <Select
                              label="انبار پیش‌فرض"
                              options={[
                                { value: '', label: 'انتخاب کنید' },
                                // Populated from API
                              ]}
                              placeholder="انبار پیش‌فرض"
                              {...field}
                            />
                          )}
                        />
                      </div>

                      <div className="pt-4 border-t border-neutral-200 dark:border-neutral-800">
                        <h4 className="font-medium text-neutral-900 dark:text-white mb-3">تنظیمات پیشرفته ردیابی</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <label className="flex items-center gap-2 p-3 rounded-xl border border-neutral-200 dark:border-neutral-800 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
                            <Controller
                              name="serial_tracked"
                              control={control}
                              render={({ field }) => (
                                <input type="checkbox" className="w-5 h-5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500" {...field} />
                              )}
                            />
                            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">ردیابی سریال</span>
                          </label>
                          <label className="flex items-center gap-2 p-3 rounded-xl border border-neutral-200 dark:border-neutral-800 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
                            <Controller
                              name="batch_tracked"
                              control={control}
                              render={({ field }) => (
                                <input type="checkbox" className="w-5 h-5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500" {...field} />
                              )}
                            />
                            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">ردیابی بچ/لذت</span>
                          </label>
                          <label className="flex items-center gap-2 p-3 rounded-xl border border-neutral-200 dark:border-neutral-800 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
                            <Controller
                              name="expiry_tracked"
                              control={control}
                              render={({ field }) => (
                                <input type="checkbox" className="w-5 h-5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500" {...field} />
                              )}
                            />
                            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">ردیابی انقضا</span>
                          </label>
                          <label className="flex items-center gap-2 p-3 rounded-xl border border-neutral-200 dark:border-neutral-800 cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors md:col-span-3">
                            <Controller
                              name="allow_backorder"
                              control={control}
                              render={({ field }) => (
                                <input type="checkbox" className="w-5 h-5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500" {...field} />
                              )}
                            />
                            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">اجازه سفارش پیش‌فروش (Backorder) وقتی موجودی صفر است</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 5: Advanced */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="card p-6">
                  <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
                    <Settings className="w-5 h-5 text-primary-600" />
                    تنظیمات پیشرفته
                  </h3>

                  <div className="space-y-4">
                    <Controller
                      name="seo_title"
                      control={control}
                      render={({ field }) => (
                        <Input
                          label="عنوان SEO"
                          placeholder="عنوان صفحه در نتایج جستجو (حداکثر ۶۰ کاراکتر)"
                          maxLength={60}
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="seo_description"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          label="توضیحات SEO"
                          placeholder="توضیحات صفحه در نتایج جستجو (حداکثر ۱۶۰ کاراکتر)"
                          rows={3}
                          maxLength={160}
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      name="tags"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">برچسب‌ها</label>
                          <div className="flex flex-wrap gap-2">
                            {(field.value || []).map((tag, i) => (
                              <Badge key={i} variant="primary" className="gap-1" onClose={() => {
                                const newTags = [...(field.value || [])]
                                newTags.splice(i, 1)
                                field.onChange(newTags)
                              }}>
                                {tag}
                              </Badge>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <Input
                              placeholder="افزودن برچسب و Enter بزنید"
                              className="flex-1"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                                  const newTags = [...(field.value || []), e.currentTarget.value.trim()]
                                  field.onChange(newTags)
                                  e.currentTarget.value = ''
                                }
                              }}
                            />
                          </div>
                        </div>
                      )}
                    />
                    <Controller
                      name="notes"
                      control={control}
                      render={({ field }) => (
                        <Textarea
                          label="یادداشت‌های داخلی"
                          placeholder="یادداشت‌هایی که فقط کارکنان می‌بینند..."
                          rows={4}
                          {...field}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Summary */}
                <div className="card p-6 bg-neutral-50 dark:bg-neutral-800/50">
                  <h4 className="font-medium text-neutral-900 dark:text-white mb-4">خلاصه کالا</h4>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div><dt className="text-neutral-500 dark:text-neutral-400">نام</dt><dd className="font-medium text-neutral-900 dark:text-white">{watch('name') || '—'}</dd></div>
                    <div><dt className="text-neutral-500 dark:text-neutral-400">SKU</dt><dd className="font-medium text-neutral-900 dark:text-white font-mono">{watch('sku') || '—'}</dd></div>
                    <div><dt className="text-neutral-500 dark:text-neutral-400">نوع</dt><dd className="font-medium text-neutral-900 dark:text-white">{productType}</dd></div>
                    <div><dt className="text-neutral-500 dark:text-neutral-400">تنوع‌ها</dt><dd className="font-medium text-neutral-900 dark:text-white">{productType === 'simple' ? 1 : variantFields.length}</dd></div>
                    <div><dt className="text-neutral-500 dark:text-neutral-400">قیمت فروش</dt><dd className="font-medium text-neutral-900 dark:text-white">{productType === 'simple' ? formatCurrency(watch('sale_price') || 0) : 'متعدد'}</dd></div>
                    <div><dt className="text-neutral-500 dark:text-neutral-400">موجودی</dt><dd className="font-medium text-neutral-900 dark:text-white">{trackStock ? (productType === 'simple' ? watch('current_stock') : variantFields.reduce((sum, v) => sum + (watch(`variants.${v}.current_stock`) || 0), 0)) : 'بدون رصد'}</dd></div>
                    <div><dt className="text-neutral-500 dark:text-neutral-400">تصاویر</dt><dd className="font-medium text-neutral-900 dark:text-white">{images.length} اصلی + {Object.values(variantImages).flat().length} تنوع</dd></div>
                    <div><dt className="text-neutral-500 dark:text-neutral-400">برچسب‌ها</dt><dd className="font-medium text-neutral-900 dark:text-white">{(watch('tags') || []).length}</dd></div>
                  </dl>
                </div>
              </div>
            )}
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="fixed bottom-0 inset-x-0 z-30 bg-white/95 dark:bg-neutral-900/95 backdrop-blur-md border-t border-neutral-200 dark:border-neutral-800 px-4 sm:px-6 py-3.5 shadow-lg">
            <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
              <Button
                variant="ghost"
                onClick={prevStep}
                disabled={currentStep === 0}
                type="button"
                className="gap-2"
              >
                <ArrowRight className="w-4 h-4" />
                <span>بازگشت</span>
              </Button>

              <div className="text-center text-xs sm:text-sm font-medium text-neutral-500 dark:text-neutral-400">
                مرحله {currentStep + 1} از {steps.length}
              </div>

              {currentStep === steps.length - 1 ? (
                <Button
                  type="submit"
                  disabled={createMutation.isPending || isSubmitting}
                  variant="primary"
                  className="gap-2"
                >
                  {createMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      در حال ایجاد...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      ایجاد کالا
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={nextStep}
                  type="button"
                  className="gap-2"
                >
                  <span>ادامه</span>
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </form>
    </div>
  )
}